import dayjs from "dayjs";
import {
  ReprintAuthRow,
  ReprintAuthStatus,
  ReprintTransactionType,
} from "../../types/reprintAuth.type";

// Charcoal is used in the approved prototype for headings/emphasis. It is not
// in the shared MUI palette, so it lives here as a literal.
export const CHARCOAL = "#363A44";
export const AMBER = "#FDA42C"; // = theme.palette.warning.main

export const STATUS_FILTER = [
  { label: "ทุกสถานะ", value: "all" },
  { label: "ใช้งานได้", value: "active" },
  { label: "หมดอายุ", value: "expired" },
  { label: "ยกเลิกแล้ว", value: "revoked" },
];

// Client-side risk view (advisory only — no backend limit).
export const RISK_VIEW_FILTER = [
  { label: "ทั้งหมด", value: "all" },
  { label: "ใกล้หมดอายุ", value: "near_expiry" },
  { label: "พิมพ์ซ้ำเกินกำหนด", value: "over_print" },
];

export type RiskView = "all" | "near_expiry" | "over_print";

export const TRANSACTION_TYPE_LABEL: Record<ReprintTransactionType, string> = {
  invoice: "เครดิต",
  cashsale: "ขายสด",
};

// created_by_role from the backend is the raw Keycloak group string
// (e.g. "branch manager"); map it to a Thai label for display.
const ROLE_LABEL: Record<string, string> = {
  "branch manager": "ผู้จัดการสาขา",
  "branch finance": "การเงินสาขา",
  "branch accounting": "บัญชีสาขา",
  "branch finance and accounting": "การเงินและบัญชีสาขา",
  "ho accounting": "บัญชีสำนักงานใหญ่",
  "ho accounting supervisor": "หัวหน้าบัญชีสำนักงานใหญ่",
  "branch checker": "ผู้ตรวจสอบสาขา",
  "branch sales person": "พนักงานขาย",
  "bdc manager": "หัวหน้า BDC",
  "bdc operator": "เจ้าหน้าที่ BDC",
  marketing: "การตลาด",
  admin: "ผู้ดูแลระบบ",
  master: "Master",
};

export const roleLabel = (role?: string | null): string => {
  if (!role) return "-";
  return ROLE_LABEL[role.toLowerCase()] ?? role;
};

type StatusChipColor = "success" | "default" | "error";

export const STATUS_CHIP: Record<
  ReprintAuthStatus,
  { label: string; color: StatusChipColor }
> = {
  ACTIVE: { label: "ใช้งานได้", color: "success" },
  EXPIRED: { label: "หมดอายุ", color: "default" },
  REVOKED: { label: "ยกเลิกแล้ว", color: "error" },
};

// A grant that has printed everything it allows stays ACTIVE — nothing transitions it,
// its window just runs out — so the raw status chip would keep promising «ใช้งานได้» on a
// grant that can no longer print a sheet. Show what is actually true instead.
export const statusChipOf = (
  row: ReprintAuthRow,
): { label: string; color: StatusChipColor } =>
  row.status === "ACTIVE" && row.quota_exhausted === true
    ? { label: "ใช้ครบแล้ว", color: "default" }
    : STATUS_CHIP[row.status];

const NEAR_EXPIRY_MS = 3 * 3600 * 1000; // 3 hours
// "พิมพ์ซ้ำเกินกำหนด": an ACTIVE grant already reprinted 2+ times. Advisory,
// there is NO backend enforcement of this limit.
const OVER_PRINT_THRESHOLD = 2;

export const getRemainingMs = (validUntil: string) =>
  dayjs(validUntil).diff(dayjs());

// Near-expiry: ACTIVE grant whose remaining time is within 3h (orange highlight).
export const isNearExpiry = (row: ReprintAuthRow): boolean => {
  const ms = getRemainingMs(row.valid_until);
  return row.status === "ACTIVE" && ms > 0 && ms <= NEAR_EXPIRY_MS;
};

// Over-print watch: ACTIVE grant with reprint_count >= 2 (red highlight).
export const isOverPrint = (row: ReprintAuthRow): boolean =>
  row.status === "ACTIVE" && row.reprint_count >= OVER_PRINT_THRESHOLD;

export const isRisky = (row: ReprintAuthRow): boolean =>
  isNearExpiry(row) || isOverPrint(row);

// Row background highlight — over-print (red) wins over near-expiry (orange).
export const riskRowBg = (row: ReprintAuthRow): string | undefined => {
  if (isOverPrint(row)) return "#fb3b3b0f";
  if (isNearExpiry(row)) return "#fda42c14";
  return undefined;
};

// Remaining-time text for the "หมดอายุ" column.
export const getRemainingText = (row: ReprintAuthRow): string => {
  if (row.status === "REVOKED") return "-";
  const ms = getRemainingMs(row.valid_until);
  if (ms <= 0) return "หมดอายุแล้ว";
  const h = ms / 3600000;
  if (h < 1) return `อีก ${Math.round(ms / 60000)} นาที`;
  if (h < 48) return `อีก ${Math.round(h)} ชม.`;
  return `อีก ${Math.round(h / 24)} วัน`;
};

// e.g. copy_set ["A","B"] -> "ชุด A + B". Null/empty -> "-".
export const getCopySetLabel = (copySet: string[] | null): string =>
  copySet && copySet.length > 0 ? `ชุด ${copySet.join(" + ")}` : "-";

// Distinct created-by options derived from a sample of grants (no dedicated
// endpoint exists). Advisory filter — reflects creators seen in the sample.
export interface CreatedByOption {
  value: string;
  label: string;
}

export const buildCreatedByOptions = (
  rows: ReprintAuthRow[],
): CreatedByOption[] => {
  const map = new Map<string, string>();
  rows.forEach((r) => {
    if (r.created_by && !map.has(r.created_by)) {
      map.set(r.created_by, r.created_by_name || r.created_by);
    }
  });
  return Array.from(map.entries()).map(([value, label]) => ({ value, label }));
};

// Distinct route options derived from a sample of grants.
export const buildRouteOptions = (rows: ReprintAuthRow[]): string[] =>
  Array.from(
    new Set(rows.map((r) => r.route_code).filter((c): c is string => !!c)),
  ).sort();
